import React, { useState, useEffect, useRef, useCallback } from 'react';
import Papa from 'papaparse'; // Add this import for CSV export

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function Leaderboards() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    
    // Regular leaderboards state
    const [leaderboards, setLeaderboards] = useState([]);
    
    // Rollup leaderboards state
    const [rollupLeaderboards, setRollupLeaderboards] = useState([]);
    
    // Combined state
    const [leaderboardType, setLeaderboardType] = useState('regular'); // 'regular' or 'rollup'
    const [selectedLeaderboardID, setSelectedLeaderboardID] = useState('');
    const [scores, setScores] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState(null);
    const refreshTimer = useRef(null);
    const [isTimeBased, setIsTimeBased] = useState(true);
    const [slideshowMode, setSlideshowMode] = useState(false);
    const [isTeamBased, setIsTeamBased] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Helper function to check if scores are team-based
    const checkIfTeamBased = (scoresArray) => {
        if (!Array.isArray(scoresArray) || scoresArray.length === 0) return false;
        
        // Check if TeamName property exists and is populated in at least some scores
        return scoresArray.some(score => score.TeamName && score.TeamName.trim() !== '');
    };
    
    // Helper function to group scores by team
    const groupScoresByTeam = (scoresArray) => {
        if (!Array.isArray(scoresArray) || scoresArray.length === 0) return [];
        
        const teamGroups = {};
        const resultArray = [];
        
        // Group scores by team name
        scoresArray.forEach(score => {
            const teamName = score.TeamName || 'Unassigned';
            if (!teamGroups[teamName]) {
                teamGroups[teamName] = [];
            }
            teamGroups[teamName].push(score);
        });
        
        // Create the hierarchical structure
        Object.keys(teamGroups).forEach(teamName => {
            const teamMembers = teamGroups[teamName];
            if (teamMembers.length > 0) {
                // Add a team row (using the first member's data for the team score)
                const teamData = { 
                    ...teamMembers[0],
                    isTeamRow: true,
                    memberCount: teamMembers.length,
                    teamRowId: `team-${teamName.replace(/\s+/g, '-')}`
                };
                resultArray.push(teamData);
                
                // Add all team members
                teamMembers.forEach(member => {
                    resultArray.push({
                        ...member,
                        isTeamMember: true,
                        parentTeam: teamName
                    });
                });
            }
        });
        
        return resultArray;
    };

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                // Removed filtering for Enabled events
                setEvents(Array.isArray(data) ? data : []);
                
                // Auto-select first event
                if (Array.isArray(data) && data.length > 0) {
                    setSelectedEventID(data[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (!selectedEventID) return;
        
        // Reset selections and state
        setSelectedLeaderboardID('');
        setLeaderboardType('regular');
        setRefreshInterval(null);
        setSlideshowMode(false);
        setScores([]);
        
        // Fetch regular leaderboards
        const fetchLeaderboards = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllLeaderboards?EventID=${selectedEventID}`);
                const data = await response.json();
                console.log(`Fetched regular leaderboards for EventID ${selectedEventID}:`, data);
                // Removed filtering for Enabled leaderboards
                setLeaderboards(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching regular leaderboards:', error);
                setLeaderboards([]);
            }
        };
        
        // Fetch rollup leaderboards
        const fetchRollupLeaderboards = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllRollupLeaderboards?EventID=${selectedEventID}`);
                const data = await response.json();
                console.log(`Fetched rollup leaderboards for EventID ${selectedEventID}:`, data);
                // Removed filtering for Enabled rollup leaderboards
                setRollupLeaderboards(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching rollup leaderboards:', error);
                setRollupLeaderboards([]);
            }
        };
        
        // Execute both fetch operations
        Promise.all([fetchLeaderboards(), fetchRollupLeaderboards()]);
    }, [selectedEventID]);

    // Helper function to get the next leaderboard ID in the sequence, considering both types
    const getNextLeaderboardID = useCallback(() => {
        const regularBoards = leaderboards || [];
        const rollupBoards = rollupLeaderboards || [];
        
        // If there are no leaderboards, return empty
        if (regularBoards.length === 0 && rollupBoards.length === 0) return { id: '', type: 'regular' };
        
        // If no leaderboard is selected, start with the first regular one if available
        if (!selectedLeaderboardID) {
            if (regularBoards.length > 0) {
                return { id: regularBoards[0].CompetitionID.toString(), type: 'regular' };
            } else if (rollupBoards.length > 0) {
                return { id: rollupBoards[0].RollupLeaderboardID.toString(), type: 'rollup' };
            }
            return { id: '', type: 'regular' };
        }
        
        // Current selection is a regular leaderboard
        if (leaderboardType === 'regular') {
            const currentIndex = regularBoards.findIndex(lb => lb.CompetitionID === Number(selectedLeaderboardID));
            
            // If we're at the end of regular leaderboards, move to rollup or cycle back
            if (currentIndex === regularBoards.length - 1 || currentIndex === -1) {
                if (rollupBoards.length > 0) {
                    // Move to the first rollup leaderboard
                    return { id: rollupBoards[0].RollupLeaderboardID.toString(), type: 'rollup' };
                } else {
                    // Cycle back to first regular leaderboard
                    return { id: regularBoards[0].CompetitionID.toString(), type: 'regular' };
                }
            } else {
                // Move to next regular leaderboard
                return { 
                    id: regularBoards[currentIndex + 1].CompetitionID.toString(), 
                    type: 'regular' 
                };
            }
        } 
        // Current selection is a rollup leaderboard
        else {
            const currentIndex = rollupBoards.findIndex(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
            
            // If we're at the end of rollup leaderboards, move to regular or cycle back
            if (currentIndex === rollupBoards.length - 1 || currentIndex === -1) {
                if (regularBoards.length > 0) {
                    // Move to the first regular leaderboard
                    return { id: regularBoards[0].CompetitionID.toString(), type: 'regular' };
                } else {
                    // Cycle back to first rollup leaderboard
                    return { id: rollupBoards[0].RollupLeaderboardID.toString(), type: 'rollup' };
                }
            } else {
                // Move to next rollup leaderboard
                return { 
                    id: rollupBoards[currentIndex + 1].RollupLeaderboardID.toString(), 
                    type: 'rollup' 
                };
            }
        }
    }, [leaderboards, rollupLeaderboards, selectedLeaderboardID, leaderboardType]);

    // Handle leaderboard selection change
    const handleLeaderboardChange = (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedLeaderboardID('');
            return;
        }
        
        // Clear existing scores and reset team-based state
        setScores([]);
        setIsTeamBased(false);
        
        const [type, id] = value.split('|');
        setLeaderboardType(type);
        setSelectedLeaderboardID(id);
    };

    // Handle slideshow mode toggle
    const handleSlideshowToggle = (e) => {
        const isChecked = e.target.checked;
        setSlideshowMode(isChecked);
        
        // If enabling slideshow with no leaderboard selected, select the first available one
        if (isChecked && !selectedLeaderboardID) {
            const regularAvailable = leaderboards.length > 0;
            const rollupAvailable = rollupLeaderboards.length > 0;
            
            if (regularAvailable) {
                setLeaderboardType('regular');
                setSelectedLeaderboardID(leaderboards[0].CompetitionID.toString());
            } else if (rollupAvailable) {
                setLeaderboardType('rollup');
                setSelectedLeaderboardID(rollupLeaderboards[0].RollupLeaderboardID.toString());
            }
        }
    };

    useEffect(() => {
        if (!selectedLeaderboardID) return;
        
        // Clear existing scores and set loading state
        setScores([]);
        setIsLoading(true);
        
        if (leaderboardType === 'regular') {
            const selectedLeaderboard = leaderboards.find(lb => lb.CompetitionID === Number(selectedLeaderboardID));
            if (selectedLeaderboard) {
                setIsTimeBased(selectedLeaderboard.TimeBased);
                setRefreshInterval(selectedLeaderboard.RefreshInterval || 30);
                fetchRegularScores(selectedLeaderboardID);
            }
        } else { // rollup
            const selectedRollupLeaderboard = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
            if (selectedRollupLeaderboard) {
                setRefreshInterval(selectedRollupLeaderboard.RefreshInterval || 30);
                fetchRollupScores(selectedLeaderboardID);
            }
        }
    }, [selectedLeaderboardID, leaderboardType, leaderboards, rollupLeaderboards]);

    useEffect(() => {
        if (refreshInterval && selectedLeaderboardID) {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
            
            refreshTimer.current = setInterval(async () => {
                if (slideshowMode) {
                    // In slideshow mode, move to the next leaderboard
                    const next = getNextLeaderboardID();
                    console.log(`Slideshow: switching to ${next.type} leaderboard ${next.id}`);
                    setLeaderboardType(next.type);
                    setSelectedLeaderboardID(next.id);
                } else {
                    // Normal refresh mode - refresh current leaderboard
                    if (leaderboardType === 'regular') {
                        await fetchRegularScores(selectedLeaderboardID);
                    } else {
                        await fetchRollupScores(selectedLeaderboardID);
                    }
                }
            }, refreshInterval * 1000);

            return () => {
                clearInterval(refreshTimer.current);
            };
        }
    }, [refreshInterval, selectedLeaderboardID, leaderboardType, leaderboards, rollupLeaderboards, slideshowMode, getNextLeaderboardID]);

    const fetchRegularScores = async (leaderboardID) => {
        try {
            console.log(`Fetching regular scores for leaderboard: ${leaderboardID}`);
            setIsLoading(true);
            
            // Reset state before fetching new data
            setScores([]);
            setIsTeamBased(false);
            
            const response = await fetch(`${API_BASE_URL}/getLeaderboardScores?CompetitionID=${leaderboardID}`);
            const data = await response.json();
            const scoresArray = Array.isArray(data) ? data : [];
            
            // Check if the leaderboard is team-based
            const teamBased = checkIfTeamBased(scoresArray);
            setIsTeamBased(teamBased);
            
            // If team-based, group by teams
            if (teamBased) {
                setScores(groupScoresByTeam(scoresArray));
            } else {
                setScores(scoresArray);
            }
        } catch (error) {
            console.error('Error fetching regular scores:', error);
            setScores([]);
            setIsTeamBased(false);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchRollupScores = async (rollupLeaderboardID) => {
        try {
            console.log(`Fetching rollup scores for leaderboard: ${rollupLeaderboardID}`);
            setIsLoading(true);
            
            // Reset state before fetching new data
            setScores([]);
            setIsTeamBased(false);
            
            const response = await fetch(`${API_BASE_URL}/getRollupLeaderboardScores?rollupLeaderboardID=${rollupLeaderboardID}`);
            const data = await response.json();
            setScores(Array.isArray(data) ? data : []);
            setIsTeamBased(false); // Rollup leaderboards aren't team-based currently
        } catch (error) {
            console.error('Error fetching rollup scores:', error);
            setScores([]);
            setIsTeamBased(false);
        } finally {
            setIsLoading(false);
        }
    };

    const displayValue = (value) => (value === null || value === undefined || value === '' ? '-' : value);

    const formatTime = (minutes, seconds) => {
        if ((minutes === null || minutes === undefined) && (seconds === null || seconds === undefined)) return '-';
        const mm = minutes !== null && minutes !== undefined ? String(minutes).padStart(2, '0') : '00';
        if (seconds === null || seconds === undefined) return `${mm}:00`;
        const [secInt, secDec] = String(seconds).split('.');
        const ss = secInt.padStart(2, '0');
        return secDec !== undefined ? `${mm}:${ss}.${secDec}` : `${mm}:${ss}`;
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined || num === '') return '-';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const getRollupRankColumns = (scoresArray) => {
        if (!Array.isArray(scoresArray) || scoresArray.length === 0) return [];
        return Object.keys(scoresArray[0]).filter(key => key.startsWith('Rank in '));
    };

    const getRollupMetricLabel = () => {
        if (leaderboardType !== 'rollup') return 'Rollup Points';
        const selectedRollup = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
        return selectedRollup?.RollupMethod === 'Average' ? 'Average Score' : 'Rollup Points';
    };

    const shouldShowRollupRankColumns = () => {
        const selectedRollup = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
        if (!selectedRollup) return true;
        return selectedRollup.ShowCompetitionRanks !== false;
    };

    // Add CSV export function
    const exportToCSV = () => {
        if (!scores.length || !selectedLeaderboardID) return;
        
        let csvData = [];
        const fileName = `${getLeaderboardName().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        
        if (leaderboardType === 'rollup') {
            // Format rollup leaderboard data
            const isCompetitorBased = scores[0]?.CompetitorID !== undefined;
            const isOrganizationBased = scores[0]?.Organization !== undefined;
            const rankColumns = getRollupRankColumns(scores);
            const includeRankColumns = shouldShowRollupRankColumns();
            
            if (isCompetitorBased) {
                csvData = scores.map(score => {
                    const data = {
                        OverallRank: score.OverallRank,
                        CompetitorID: score.CompetitorID,
                        CompetitorName: score.CompetitorName,
                        Organization: score.Organization || 'N/A',
                        [getRollupMetricLabel()]: score.TotalRollupPoints
                    };
                    if (!shouldHideColumn('CompetitorState')) data.State = score.CompetitorState || 'N/A';
                    if (!shouldHideColumn('Division')) data.Division = score.Division || 'N/A';
                    if (includeRankColumns) {
                        rankColumns.forEach(column => {
                            if (!shouldHideColumn(column)) {
                                data[column] = score[column] ?? '';
                            }
                        });
                    }
                    return data;
                });
            } else if (isOrganizationBased) {
                csvData = scores.map(score => ({
                    OverallRank: score.OverallRank,
                    Organization: score.Organization,
                    [getRollupMetricLabel()]: score.TotalRollupPoints,
                    ...(includeRankColumns ? rankColumns.reduce((acc, column) => {
                        if (!shouldHideColumn(column)) {
                            acc[column] = score[column] ?? '';
                        }
                        return acc;
                    }, {}) : {})
                }));
            }
        } else {
            // Format regular leaderboard data - filter out team member rows for CSV
            const exportScores = isTeamBased 
                ? scores.filter(score => !score.isTeamMember) 
                : scores;
                
            csvData = exportScores.map(score => {
                const data = {
                    Rank: score.Placement
                };
                
                if (isTeamBased) data.TeamName = score.TeamName;
                if (!shouldHideColumn('CompetitorID') && !score.isTeamRow) data.CompetitorID = score.CompetitorID;
                if (!shouldHideColumn('CompetitorName') && !score.isTeamRow) data.CompetitorName = score.CompetitorName;
                if (!shouldHideColumn('Organization') && !score.isTeamRow) data.Organization = score.Organization;
                if (!shouldHideColumn('CompetitorState') && !score.isTeamRow) data.State = score.CompetitorState;
                if (!shouldHideColumn('Division') && !score.isTeamRow) data.Division = score.Division;
                if (!shouldHideColumn('AttemptNumber') && !score.isTeamMember) data.Attempt = score.AttemptNumber;
                
                if (!shouldHideColumn('Minutes') && !shouldHideColumn('Seconds') && !score.isTeamMember) {
                    data.Time = formatTime(score.Minutes, score.Seconds);
                }
                
                if (!shouldHideColumn('BaseScore') && !score.isTeamMember) data.BaseScore = score.BaseScore;
                if (!shouldHideColumn('BonusesPenalties') && !score.isTeamMember) data.BonusesPenalties = score.BonusesPenalties;
                if (!shouldHideColumn('FinalScore') && !score.isTeamMember) data.FinalScore = score.FinalScore;
                
                return data;
            });
        }
        
        // Generate CSV using PapaParse
        const csv = Papa.unparse(csvData);
        
        // Create and download the CSV file
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const shouldHideColumn = (column) => {
        if (leaderboardType === 'rollup') {
            if (column.startsWith('Rank in ') && !shouldShowRollupRankColumns()) {
                return true;
            }
            // Different logic for rollup leaderboards
            return scores.every(score => score[column] === null || score[column] === undefined);
        }
        
        // Regular leaderboard logic
        if (!isTimeBased && ['Minutes', 'Seconds', 'BaseScore', 'BonusesPenalties'].includes(column)) {
            return true; // Hide time-related columns if TimeBased is false
        }
        
        // Check if all values are null for this column
        return scores.every(score => score[column] === null || score[column] === undefined);
    };

    // Get the current leaderboard name based on type and ID
    const getLeaderboardName = () => {
        if (!selectedLeaderboardID) return '';
        
        if (leaderboardType === 'regular') {
            const lb = leaderboards.find(lb => lb.CompetitionID === Number(selectedLeaderboardID));
            return lb?.CompetitionName || '';
        } else {
            const lb = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
            return lb?.RollupLeaderboardName || '';
        }
    };

    // Render table based on leaderboard type
    const renderTable = () => {
        if (isLoading) {
            return <p>Loading scores...</p>;
        }
        
        if (!selectedLeaderboardID || scores.length === 0) {
            return <p>No scores available</p>;
        }

        if (leaderboardType === 'rollup') {
            // Format rollup leaderboard data
            const isCompetitorBased = scores[0]?.CompetitorID !== undefined;
            const isOrganizationBased = scores[0]?.Organization !== undefined;
            const rankColumns = getRollupRankColumns(scores);
            const includeRankColumns = shouldShowRollupRankColumns();
            
            return (
                <table className="data-table">
                    <thead>
                        {isCompetitorBased ? (
                            <tr>
                                <th>Rank</th>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Organization</th>
                                {!shouldHideColumn('CompetitorState') && <th>State</th>}
                                {!shouldHideColumn('Division') && <th>Division</th>}
                                <th>{getRollupMetricLabel()}</th>
                                {includeRankColumns && rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                    <th key={column}>{column}</th>
                                ))}
                            </tr>
                        ) : isOrganizationBased ? (
                            <tr>
                                <th>Rank</th>
                                <th>Organization</th>
                                <th>{getRollupMetricLabel()}</th>
                                {includeRankColumns && rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                    <th key={column}>{column}</th>
                                ))}
                            </tr>
                        ) : null}
                    </thead>
                    <tbody>
                        {scores.map((score, index) =>
                            isCompetitorBased ? (
                                <tr key={score.CompetitorID || index} style={{ borderBottom: '2px solid #000' }}>
                                    <td>{displayValue(score.OverallRank)}</td>
                                    <td>{displayValue(score.CompetitorID)}</td>
                                    <td>{displayValue(score.CompetitorName)}</td>
                                    <td>{displayValue(score.Organization)}</td>
                                    {!shouldHideColumn('CompetitorState') && <td>{displayValue(score.CompetitorState)}</td>}
                                    {!shouldHideColumn('Division') && <td>{displayValue(score.Division)}</td>}
                                    <td>{formatNumber(score.TotalRollupPoints)}</td>
                                    {includeRankColumns && rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                        <td key={column}>{displayValue(score[column])}</td>
                                    ))}
                                </tr>
                            ) : isOrganizationBased ? (
                                <tr key={index} style={{ borderBottom: '2px solid #000' }}>
                                    <td>{displayValue(score.OverallRank)}</td>
                                    <td>{displayValue(score.Organization)}</td>
                                    <td>{formatNumber(score.TotalRollupPoints)}</td>
                                    {includeRankColumns && rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                        <td key={column}>{displayValue(score[column])}</td>
                                    ))}
                                </tr>
                            ) : null
                        )}
                    </tbody>
                </table>
            );
        } else {
            // Regular leaderboard table with team-based display support
            return (
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Rank</th>
                            {isTeamBased && <th>Team</th>}
                            {!shouldHideColumn('CompetitorID') && <th>ID</th>}
                            {!shouldHideColumn('CompetitorName') && <th>Name</th>}
                            {!shouldHideColumn('Organization') && <th>Organization</th>}
                            {!shouldHideColumn('CompetitorState') && <th>State</th>}
                            {!shouldHideColumn('Division') && <th>Division</th>}
                            {!shouldHideColumn('AttemptNumber') && <th>Attempt #</th>}
                            {!shouldHideColumn('Minutes') && !shouldHideColumn('Seconds') && <th>Time</th>}
                            {!shouldHideColumn('BaseScore') && <th>Seconds</th>}
                            {!shouldHideColumn('BonusesPenalties') && <th>Bonuses/Penalties ±</th>}
                            {!shouldHideColumn('FinalScore') && <th>Score</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {scores.map((score, index) => {
                            // Style for team rows
                            const rowStyle = score.isTeamRow ? {
                                backgroundColor: '#e6f7ff',
                                fontWeight: 'bold',
                                borderBottom: '1px solid #a8d4f5',
                                borderTop: '2px solid #0082d4'
                            } : score.isTeamMember ? {
                                paddingLeft: '20px',
                                backgroundColor: '#f9fdff',
                                borderBottom: '1px solid #eaeaea'
                            } : {
                                borderBottom: '2px solid #000'
                            };
                            
                            return (
                                <tr key={score.isTeamRow ? score.teamRowId : (score.CompetitorID || index)} style={rowStyle}>
                                    <td>{score.isTeamMember ? '' : displayValue(score.Placement)}</td>
                                    {isTeamBased && (
                                        <td>
                                            {score.isTeamRow ? (
                                                <strong>{score.TeamName} ({score.memberCount})</strong>
                                            ) : score.isTeamMember ? (
                                                <span className="team-member-indicator">→</span>
                                            ) : displayValue(score.TeamName)}
                                        </td>
                                    )}
                                    {!shouldHideColumn('CompetitorID') && (
                                        <td>{score.isTeamRow ? '' : displayValue(score.CompetitorID)}</td>
                                    )}
                                    {!shouldHideColumn('CompetitorName') && (
                                        <td>{score.isTeamRow ? '' : displayValue(score.CompetitorName)}</td>
                                    )}
                                    {!shouldHideColumn('Organization') && (
                                        <td>{score.isTeamRow ? '' : displayValue(score.Organization)}</td>
                                    )}
                                    {!shouldHideColumn('CompetitorState') && (
                                        <td>{score.isTeamRow ? '' : displayValue(score.CompetitorState)}</td>
                                    )}
                                    {!shouldHideColumn('Division') && (
                                        <td>{score.isTeamRow ? '' : displayValue(score.Division)}</td>
                                    )}
                                    {!shouldHideColumn('AttemptNumber') && (
                                        <td>{score.isTeamMember ? '' : displayValue(score.AttemptNumber)}</td>
                                    )}
                                    {!shouldHideColumn('Minutes') && !shouldHideColumn('Seconds') && (
                                        <td>{score.isTeamMember ? '' : formatTime(score.Minutes, score.Seconds)}</td>
                                    )}
                                    {!shouldHideColumn('BaseScore') && (
                                        <td>{score.isTeamMember ? '' : formatNumber(score.BaseScore)}</td>
                                    )}
                                    {!shouldHideColumn('BonusesPenalties') && (
                                        <td>{score.isTeamMember ? '' : formatNumber(score.BonusesPenalties)}</td>
                                    )}
                                    {!shouldHideColumn('FinalScore') && (
                                        <td>{score.isTeamMember ? '' : formatNumber(score.FinalScore)}</td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        }
    };

    // Create combined leaderboard options
    const getLeaderboardOptions = () => {
        const regularOptions = Array.isArray(leaderboards) ? leaderboards.map(lb => ({
            value: `regular|${lb.CompetitionID}`,
            label: `🏆 ${lb.CompetitionName}`
        })) : [];
        
        const rollupOptions = Array.isArray(rollupLeaderboards) ? rollupLeaderboards.map(lb => ({
            value: `rollup|${lb.RollupLeaderboardID}`,
            label: `📊 ${lb.RollupLeaderboardName}`
        })) : [];
        
        return [...regularOptions, ...rollupOptions];
    };

    return (
        <div className="p-1">
            <div className="flex items-center mb-1 gap-1">
                <label>
                    <select className="form-select" value={selectedEventID} onChange={(e) => setSelectedEventID(e.target.value)}>
                        <option value="">Select Event</option>
                        {Array.isArray(events) && events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>
                
                <label>
                    <select 
                        className="form-select"
                        value={selectedLeaderboardID ? `${leaderboardType}|${selectedLeaderboardID}` : ''}
                        onChange={handleLeaderboardChange}
                        disabled={!selectedEventID}
                    >
                        <option value="">Select Leaderboard</option>
                        {getLeaderboardOptions().map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
                
                {selectedEventID && (leaderboards.length > 0 || rollupLeaderboards.length > 0) && (
                    <label className="flex items-center">
                        <input 
                            type="checkbox" 
                            className="form-checkbox"
                            checked={slideshowMode}
                            onChange={handleSlideshowToggle}
                        />
                        Slideshow Mode
                    </label>
                )}
                
                {selectedLeaderboardID && scores.length > 0 && (
                    <button 
                        className="btn btn-primary" 
                        onClick={exportToCSV}
                    >
                        Export to CSV
                    </button>
                )}
            </div>
            
            {selectedLeaderboardID && (
                <div className="mt-1">
                    <div className="data-table-wrapper">
                        {renderTable()}
                    </div>
                </div>
            )}
        </div>
    );
}